import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { Alert } from "@mui/material";
import {
  disconnectIdibuIntegration,
  requestIdibuIntegrationHelp,
  saveIdibuIntegration,
  testIdibuIntegration,
} from "../../../Redux/ActionCreators/idibuAction";
import "./idibu.scss";

const EMPTY_FORM = {
  access_token: "",
  webhook_secret: "",
  iframe_app_id: "",
  iframe_app_secret: "",
  iframe_group: "",
  api_base_url: "",
};

export const STATUS_LABEL = {
  active: "Connected",
  pending: "Pending verification",
  invalid: "Invalid credentials",
  disconnected: "Not connected",
  not_connected: "Not connected",
};

export const isIdibuAccountSaved = (integration) =>
  Boolean(integration) && !["not_connected", "disconnected"].includes(integration.status || "not_connected");

const formatDate = (iso) => (iso ? new Date(iso).toLocaleString() : "-");

export const IdibuDialog = ({ title, onClose, children }) => (
  <div className="idibu-dialog-backdrop">
    <div className="idibu-dialog" role="dialog" aria-label={title}>
      <div className="idibu-dialog-head">
        <h3>{title}</h3>
        <button type="button" className="idibu-dialog-close" onClick={onClose} aria-label="Close">
          ×
        </button>
      </div>
      <div className="idibu-dialog-body">{children}</div>
    </div>
  </div>
);

/** Access token + App Secret Key (webhook secret) form. `integration` set = update mode (blank keeps stored values). */
export const IdibuConnectForm = ({ integration, onSaved, onCancel, submitLabel }) => {
  const dispatch = useDispatch();
  const isUpdate = Boolean(integration?.token_masked);
  const [form, setForm] = useState({ ...EMPTY_FORM, iframe_group: integration?.iframe_group || "" });
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });
  const keepHint = isUpdate ? "Leave blank to keep the current value" : "";

  const submit = async (e) => {
    e?.preventDefault();
    if (!isUpdate && (!form.access_token.trim() || !form.webhook_secret.trim())) {
      setError("Access token and App Secret Key are required.");
      return;
    }
    if (Boolean(form.iframe_app_id.trim()) !== Boolean(form.iframe_app_secret.trim())) {
      setError("Provide both iframe App ID and App Secret, or leave both empty.");
      return;
    }
    setSaving(true);
    setError("");
    const payload = Object.fromEntries(
      Object.entries(form).map(([k, v]) => [k, typeof v === "string" && v.trim() ? v.trim() : null])
    );
    if (isUpdate) payload.iframe_group = form.iframe_group.trim();
    const result = await dispatch(saveIdibuIntegration(payload));
    setSaving(false);
    if (result?.success) {
      onSaved?.(result.data);
    } else {
      setError(result?.message || "Failed to save idibu details");
    }
  };

  return (
    <form className="idibu-form" onSubmit={submit} autoComplete="off">
      <p className="idibu-muted">
        Find these in your idibu account under API settings. Credentials are stored encrypted and are only used
        for your organization.
      </p>
      <label className="idibu-field">
        <span>idibu Access Token {isUpdate ? "" : "*"}</span>
        <input
          type="password"
          autoComplete="new-password"
          placeholder={isUpdate ? `${integration.token_masked} — ${keepHint}` : "IDIBU ACCESS TOKEN"}
          value={form.access_token}
          onChange={set("access_token")}
        />
      </label>
      <label className="idibu-field">
        <span>idibu App Secret Key {isUpdate ? "" : "*"}</span>
        <input
          type="password"
          autoComplete="new-password"
          placeholder={isUpdate ? keepHint : "IDIBU APP SECRET KEY"}
          value={form.webhook_secret}
          onChange={set("webhook_secret")}
        />
      </label>
      <button type="button" className="idibu-link" onClick={() => setShowAdvanced((v) => !v)}>
        {showAdvanced ? "Hide advanced settings" : "Advanced settings (optional)"}
      </button>
      {showAdvanced ? (
        <>
          <label className="idibu-field">
            <span>Posting iframe App ID</span>
            <input
              type="text"
              placeholder={integration?.has_custom_iframe_app ? keepHint : "Only if idibu gave you your own App ID"}
              value={form.iframe_app_id}
              onChange={set("iframe_app_id")}
            />
          </label>
          <label className="idibu-field">
            <span>Posting iframe App Secret</span>
            <input
              type="password"
              autoComplete="new-password"
              placeholder={integration?.has_custom_iframe_app ? keepHint : ""}
              value={form.iframe_app_secret}
              onChange={set("iframe_app_secret")}
            />
          </label>
          <label className="idibu-field">
            <span>idibu user group</span>
            <input type="text" placeholder="e.g. Recruiters" value={form.iframe_group} onChange={set("iframe_group")} />
          </label>
          <label className="idibu-field">
            <span>idibu API URL</span>
            <input
              type="text"
              placeholder={integration?.api_base_url || "https://v3.idibu.com"}
              value={form.api_base_url}
              onChange={set("api_base_url")}
            />
          </label>
        </>
      ) : null}
      {error ? <Alert severity="error">{error}</Alert> : null}
      <div className="idibu-form-actions">
        {onCancel ? (
          <button type="button" className="idibu-btn-secondary" onClick={onCancel} disabled={saving}>
            Cancel
          </button>
        ) : null}
        <button type="submit" className="idibu-btn-primary" disabled={saving}>
          {saving ? "Verifying…" : submitLabel || (isUpdate ? "Save" : "Connect")}
        </button>
      </div>
    </form>
  );
};

/** "Contact Gappeo" form. Defaults to idibu setup; pass title/intro/submitAction for other integrations. */
export const ContactGappeoModal = ({
  onClose,
  onSent,
  title = "Contact Gappeo for idibu setup",
  intro = "Don't have an idibu account yet? Our team will help you set up idibu and connect it to Gappeo.",
  messagePlaceholder = "e.g. Which job boards you'd like to post to",
  submitAction = requestIdibuIntegrationHelp,
}) => {
  const dispatch = useDispatch();
  const [form, setForm] = useState({ contact_name: "", contact_email: "", contact_phone: "", message: "" });
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e?.preventDefault();
    setSending(true);
    setError("");
    const result = await dispatch(submitAction(form));
    setSending(false);
    if (result?.success) {
      onSent?.(result.data);
      onClose();
    } else {
      setError(result?.message || "Failed to send request");
    }
  };

  return (
    <IdibuDialog title={title} onClose={onClose}>
      <form className="idibu-form" onSubmit={submit}>
        <p className="idibu-muted">{intro} Leave the fields blank to use your account details.</p>
        <label className="idibu-field">
          <span>Contact name</span>
          <input
            type="text"
            placeholder="Please enter your name"
            value={form.contact_name}
            onChange={set("contact_name")}
          />
        </label>
        <label className="idibu-field">
          <span>Contact email</span>
          <input
            type="email"
            placeholder="Please enter your email"
            value={form.contact_email}
            onChange={set("contact_email")}
          />
        </label>
        <label className="idibu-field">
          <span>Phone (optional)</span>
          <input
            type="text"
            placeholder="Please enter your phone number"
            value={form.contact_phone}
            onChange={set("contact_phone")}
          />
        </label>
        <label className="idibu-field">
          <span>Message (optional)</span>
          <textarea rows={3} value={form.message} placeholder={messagePlaceholder} onChange={set("message")} />
        </label>
        {error ? <Alert severity="error">{error}</Alert> : null}
        <div className="idibu-form-actions">
          <button type="submit" className="idibu-btn-primary" disabled={sending}>
            {sending ? "Sending…" : "Send request"}
          </button>
        </div>
      </form>
    </IdibuDialog>
  );
};

export const WebhookUrlNotice = ({ url }) => {
  const [copied, setCopied] = useState(false);
  if (!url) return null;
  const copy = () => {
    navigator.clipboard.writeText(url);
    setCopied(true);
  };
  return (
    <div className="idibu-webhook">
      <span className="idibu-webhook-label">Webhook URL</span>
      <div className="idibu-webhook-row">
        <code>{url}</code>
        <button type="button" className="idibu-link" onClick={copy}>
          {copied ? "Copied" : "Copy"}
        </button>
      </div>
      <p className="idibu-muted">
        In idibu, add a webhook for candidate, post and vacancy events pointing to this URL, signed with the same
        App Secret Key. Applicants from job boards then appear in this job automatically.
      </p>
    </div>
  );
};

/** Saved idibu account: status, webhook URL and (for admins) test / update / disconnect. */
export const IdibuSettingsPanel = ({ integration, canManage, onChange }) => {
  const dispatch = useDispatch();
  const [editing, setEditing] = useState(false);
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);
  const [busy, setBusy] = useState("");
  const [message, setMessage] = useState(null);
  const status = integration?.status || "not_connected";

  const handleTest = async () => {
    setBusy("test");
    const result = await dispatch(testIdibuIntegration());
    setBusy("");
    if (result?.success) {
      onChange(result.data);
      const ok = result.data?.status === "active";
      setMessage({
        severity: ok ? "success" : "warning",
        text: ok ? "idibu connection is working" : result.data?.last_error || "idibu could not be verified",
      });
    } else {
      setMessage({ severity: "error", text: result?.message || "Connection test failed" });
    }
  };

  const handleDisconnect = async () => {
    setBusy("disconnect");
    const result = await dispatch(disconnectIdibuIntegration());
    setBusy("");
    setConfirmDisconnect(false);
    if (result?.success) {
      onChange(result.data || { connected: false, status: "not_connected" });
    } else {
      setMessage({ severity: "error", text: result?.message || "Failed to disconnect idibu" });
    }
  };

  let body;
  if (editing) {
    body = (
      <IdibuConnectForm
        integration={integration}
        onCancel={() => setEditing(false)}
        onSaved={(data) => {
          onChange(data);
          setEditing(false);
          setMessage({
            severity: data?.status === "active" ? "success" : "warning",
            text: data?.status === "active" ? "idibu details updated" : data?.last_error || "Saved; verification pending",
          });
        }}
      />
    );
  } else if (confirmDisconnect) {
    body = (
      <>
        <p>
          Stored idibu credentials will be removed and your team won't be able to post jobs to job boards until idibu
          is connected again. Existing postings and candidates are kept.
        </p>
        <div className="idibu-form-actions">
          <button type="button" className="idibu-btn-secondary" onClick={() => setConfirmDisconnect(false)}>
            Cancel
          </button>
          <button type="button" className="idibu-btn-danger" onClick={handleDisconnect} disabled={busy === "disconnect"}>
            {busy === "disconnect" ? "Disconnecting…" : "Disconnect"}
          </button>
        </div>
      </>
    );
  } else {
    body = (
      <div className="idibu-details">
        <div className="idibu-row">
          <span>Status</span>
          <span className={`idibu-badge ${status}`}>{STATUS_LABEL[status] || status}</span>
        </div>
        {status !== "active" && integration?.last_error ? (
          <Alert severity={status === "invalid" ? "error" : "warning"}>{integration.last_error}</Alert>
        ) : null}
        <div className="idibu-row">
          <span>Access token</span>
          <code>{integration?.token_masked || "-"}</code>
        </div>
        <div className="idibu-row">
          <span>App Secret Key</span>
          <span>{integration?.has_webhook_secret ? "Saved" : "Missing"}</span>
        </div>
        <div className="idibu-row">
          <span>Last verified</span>
          <span>{formatDate(integration?.last_verified_at)}</span>
        </div>
        <WebhookUrlNotice url={integration?.webhook_url} />
        {canManage ? (
          <div className="idibu-form-actions">
            <button type="button" className="idibu-btn-secondary" onClick={handleTest} disabled={busy === "test"}>
              {busy === "test" ? "Testing…" : "Test connection"}
            </button>
            <button type="button" className="idibu-btn-secondary" onClick={() => setEditing(true)}>
              Update details
            </button>
            <button type="button" className="idibu-btn-danger" onClick={() => setConfirmDisconnect(true)}>
              Disconnect
            </button>
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div className="idibu-card">
      {message ? (
        <Alert severity={message.severity} onClose={() => setMessage(null)} style={{ marginBottom: 12 }}>
          {message.text}
        </Alert>
      ) : null}
      {body}
    </div>
  );
};
